import { useState } from "react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useTranslation } from "react-i18next";
import { Loader2, RefreshCw, CalendarDays, Settings } from "lucide-react";
import { toast } from "sonner";
import { ScheduleService } from "@/features/Assistant/services/scheduleService";
import { useScheduleData } from "@/features/Assistant/hooks/useScheduleData";
import type { MeetingPeriod } from "@/features/Assistant/hooks/useScheduleData";
import { ScheduleDetailModal } from "@/features/Assistant/components/schedule/ScheduleDetailModal";
import { ActiveCallsTable } from "@/features/Assistant/components/schedule/ActiveCallsTable";
import { UpcomingMeetingsTable } from "@/features/Assistant/components/schedule/UpcomingMeetingsTable";
import { ScheduleConfirmDialogs } from "@/features/Assistant/components/schedule/ScheduleConfirmDialogs";
import { AssistantConfigPanel } from "@/features/Assistant/components/AssistantConfigPanel";

/**
 * Follow-up view of what the Prodgy Assistant decided from the calendar it reads:
 * what it is attending now, what is coming, and what it will not attend and why.
 */
export const AssistantSchedule = () => {
  const { t } = useTranslation();
  const {
    loading,
    error,
    scheduleData,
    searchTerm,
    setSearchTerm,
    detailModal,
    setDetailModal,

    blockMeetingId,
    setBlockMeetingId,
    confirmBlockMeeting,
    blockSeriesId,
    setBlockSeriesId,
    confirmBlockSeries,
    forceLeaveCallId,
    setForceLeaveCallId,
    confirmForceLeaveCall,

    meetings,
    meetingPage,
    setMeetingPage,
    meetingRowsPerPage,
    setMeetingRowsPerPage,
    meetingTotalPages,
    meetingSortField,
    meetingSortAsc,
    toggleMeetingSort,
    meetingPeriod,
    setMeetingPeriod,
    statusFilter,
    setStatusFilter,
    workspaceFilter,
    setWorkspaceFilter,
    workspaceOptions,

    calls,
    callPage,
    setCallPage,
    callRowsPerPage,
    setCallRowsPerPage,
    callTotalPages,

    seriesOccurrences,
    mailbox,
    getSortIcon,
  } = useScheduleData();

  const [configOpen, setConfigOpen] = useState(false);

  /** Recorte da janela lida. "Toda a janela" acompanha o horizonte configurado. */
  const periods: { value: MeetingPeriod; label: string }[] = [
    { value: "today", label: t("assistant.schedule.periods.today") },
    { value: "tomorrow", label: t("assistant.schedule.periods.tomorrow") },
    { value: "all", label: t("assistant.schedule.periods.all") },
  ];

  const handleSyncNow = async () => {
    try {
      await ScheduleService.syncNow();
      toast.success(t("assistant.schedule.syncRequested"));
    } catch {
      toast.error(t("assistant.schedule.actionError"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md p-4 text-sm mb-4 bg-destructive/15 border border-destructive/30 text-destructive">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6 w-full">
      <AssistantConfigPanel open={configOpen} onOpenChange={setConfigOpen} />

      {/* O título encabeça o painel inteiro — os contadores somam esta mesma lista.
          Ao lado dele, a origem (a caixa lida) e o acesso ao que a configura; a ação
          de reler a agenda fica na ponta oposta, longe dos filtros, que não agem
          sobre o servidor. */}
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">{t("assistant.schedule.calendarMeetingsTitle")}</h2>
        {mailbox && (
          <span
            title={t("assistant.schedule.mailboxHint")}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground"
          >
            <CalendarDays className="h-3 w-3" />
            {mailbox}
          </span>
        )}
        {/* Mesma linguagem de pílula do badge, mas com fundo do painel e resposta ao
            hover: o que é informação e o que é ação não devem parecer a mesma coisa. */}
        <button
          type="button"
          onClick={() => setConfigOpen(true)}
          title={t("assistant.config.title")}
          aria-label={t("assistant.config.title")}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Settings className="h-3.5 w-3.5" />
        </button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => { void handleSyncNow(); }}
          className="ml-auto"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          {t("assistant.schedule.syncNow")}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("assistant.schedule.activeCalls")}
            </p>
            {scheduleData.summary.activeCalls > 0 && (
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            )}
          </div>
          <p className="text-3xl font-bold">{scheduleData.summary.activeCalls}</p>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
            {t("assistant.schedule.upcomingMeetings")}
          </p>
          <p className="text-3xl font-bold">{scheduleData.summary.upcomingMeetings}</p>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
            {t("assistant.schedule.withError")}
          </p>
          <p
            className={`text-3xl font-bold ${scheduleData.summary.withError > 0 ? "text-destructive" : ""}`}
          >
            {scheduleData.summary.withError}
          </p>
        </div>
      </div>

      {/* Uma barra só, tudo que recorta a lista: busca, situação, workspace e período.
          A largura vai no invólucro, não no controle: `Input` e `SelectTrigger` são
          embrulhados internamente num `div.w-full`, então uma largura passada por
          className atinge só o elemento de dentro — o invólucro seguiria ocupando a
          linha inteira e empilharia os filtros. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-[260px]">
          <Input
            placeholder={t("assistant.schedule.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 bg-background"
          />
        </div>

        <div className="w-[160px]">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 bg-background" aria-label={t("assistant.schedule.filters.status")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("assistant.schedule.filters.allStatuses")}</SelectItem>
              <SelectItem value="ready">{t("assistant.schedule.statuses.ready")}</SelectItem>
              <SelectItem value="rejected">{t("assistant.schedule.statuses.rejected")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* As opções saem dos próprios workspaces presentes na janela lida: uma lista
            fixa envelheceria, e um workspace sem reunião nenhuma só ocuparia espaço. */}
        <div className="w-[190px]">
          <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
            <SelectTrigger className="h-9 bg-background" aria-label={t("assistant.schedule.filters.workspace")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("assistant.schedule.filters.allWorkspaces")}</SelectItem>
              {workspaceOptions.map((workspace) => (
                <SelectItem key={workspace} value={workspace}>
                  {workspace}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Um controle só, não três botões soltos: as opções são excludentes, e o
            contorno comum é o que diz isso sem precisar de rótulo. */}
        <div className="ml-auto inline-flex items-center rounded-md border bg-background p-0.5">
          {periods.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setMeetingPeriod(option.value)}
              aria-pressed={meetingPeriod === option.value}
              className={`rounded-[5px] px-3 py-1 text-xs font-medium transition-colors ${
                meetingPeriod === option.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <ActiveCallsTable
        calls={calls}
        totalPages={callTotalPages}
        page={callPage}
        setPage={setCallPage}
        rowsPerPage={callRowsPerPage}
        setRowsPerPage={setCallRowsPerPage}
        onDetails={(call) => setDetailModal({ type: "call", data: call })}
        onForceLeave={(callId) => setForceLeaveCallId(callId)}
      />

      <UpcomingMeetingsTable
        meetings={meetings}
        totalPages={meetingTotalPages}
        page={meetingPage}
        setPage={setMeetingPage}
        rowsPerPage={meetingRowsPerPage}
        setRowsPerPage={setMeetingRowsPerPage}
        sortField={meetingSortField}
        sortAsc={meetingSortAsc}
        onSort={toggleMeetingSort}
        getSortIcon={getSortIcon}
        seriesOccurrences={seriesOccurrences}
        onDetails={(meeting) => setDetailModal({ type: "meeting", data: meeting })}
        onBlockMeeting={(id) => setBlockMeetingId(id)}
        onBlockSeries={(seriesId) => setBlockSeriesId(seriesId)}
      />


      <ScheduleDetailModal detailModal={detailModal} onClose={() => setDetailModal(null)} />

      <ScheduleConfirmDialogs
        blockMeetingId={blockMeetingId}
        setBlockMeetingId={setBlockMeetingId}
        confirmBlockMeeting={confirmBlockMeeting}
        blockSeriesId={blockSeriesId}
        setBlockSeriesId={setBlockSeriesId}
        confirmBlockSeries={confirmBlockSeries}
        forceLeaveCallId={forceLeaveCallId}
        setForceLeaveCallId={setForceLeaveCallId}
        confirmForceLeaveCall={confirmForceLeaveCall}
      />
    </div>
  );
};
